import { NavLink } from "react-router-dom";

import { AppendixIntro } from "../components/AppendixIntro";
import { DataTableCard } from "../components/DataTableCard";
import { MetricCard } from "../components/MetricCard";
import { NumberedListCard } from "../components/NumberedListCard";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api } from "../lib/api";

type RiskPageData = Awaited<ReturnType<typeof api.getRisk>>;

function buildRiskControlRows(data: RiskPageData) {
  return [
    ["Recommendation", data.recommendation.label, data.recommendation.confidencePosture],
    ["Overall risk posture", data.riskSummary.riskPosture, "Continuation is conditional, not blind"],
    ["Legal gate", data.riskSummary.gateSummary.legal, "Explicit downside control remains live"],
    ["Site gate", data.riskSummary.gateSummary.site, "Parcel and regulatory fit still matter"],
    ["Revenue gate", data.riskSummary.gateSummary.revenue, "Commercial downside still has to stay bounded"],
    ["Risk-adjusted return gate", data.riskSummary.gateSummary.riskAdjustedReturn, "Return discipline remains a hard stop"]
  ];
}

function buildOpenRiskRows(data: RiskPageData) {
  return [
    ...data.riskSummary.remainingOpenRisks.map((item, index) => [
      `Open risk ${index + 1}`,
      item,
      "Still active"
    ]),
    ...data.recommendation.topCautions.map((item, index) => [
      `Caution ${index + 1}`,
      item,
      "Still active"
    ])
  ];
}

function buildKillRows(data: RiskPageData) {
  return data.riskSummary.killCriteriaSensitivity.map((item, index) => [
    `Kill sensitivity ${index + 1}`,
    item,
    "Continuation fails if this breaks the control stack"
  ]);
}

function buildContinuationRows(data: RiskPageData) {
  return data.recommendation.why.map((item, index) => [`Support ${index + 1}`, item]);
}

function RiskContent({ data }: { data: RiskPageData }) {
  const openControlCount = data.riskSummary.remainingOpenRisks.length + data.riskSummary.killCriteriaSensitivity.length;
  const controlRows = buildRiskControlRows(data);
  const openRiskRows = buildOpenRiskRows(data);
  const killRows = buildKillRows(data);
  const continuationRows = buildContinuationRows(data);

  return (
    <div className="stack-xl">
      <AppendixIntro
        eyebrow="Appendix / Risks"
        title="Bounded downside, not a caution memo."
        body="This page now opens with the concise downside posture: what the live risk frame is, what still remains open, and which kill conditions still control continuation. The deeper risk detail stays available below, but it no longer leads the investor path."
        noteTitle="How to use this appendix"
        note="Use this after Investment Case, Financials, and Market Support. Confirm the downside remains governable, then drop into the lower tables only if an open item or kill trigger still needs closer pressure-testing."
        actions={
          <>
            <NavLink to="/investment-case" className="link-button link-button-secondary">
              Investment Case
            </NavLink>
            <NavLink to="/financials" className="link-button link-button-secondary">
              Financials
            </NavLink>
          </>
        }
      />

      <div
        style={{
          display: "grid",
          gap: "16px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
        }}
      >
        <MetricCard title="Recommendation" value={data.recommendation.label} detail={data.recommendation.confidencePosture} />
        <MetricCard title="Risk posture" value={data.riskSummary.riskPosture} detail="Current downside summary" />
        <MetricCard title="Open items" value={`${data.riskSummary.remainingOpenRisks.length}`} detail="Visible unresolved risk items" />
        <MetricCard title="Kill conditions" value={`${data.riskSummary.killCriteriaSensitivity.length}`} detail="Explicit continuation stops still live" />
      </div>

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))"
        }}
      >
        <NumberedListCard
          eyebrow="Current Mitigants"
          title="What has already been pulled under control"
          description="These are the highest-value narrowing moves behind the current continuation posture."
          items={data.riskSummary.whatImproved}
          tone="positive"
        />
        <NumberedListCard
          eyebrow="Top Risks"
          title="What still has to stay governed"
          description="These remain active and still control conviction, but they are now finite rather than diffuse."
          items={data.riskSummary.remainingOpenRisks}
          tone="warning"
        />
      </div>

      <DataTableCard
        eyebrow="Risk Posture Matrix"
        title="Top-line downside frame"
        description="This compact table keeps the recommendation, current risk posture, and four live continuation gates in one place."
        columns={["Layer", "Current read", "Control role"]}
        rows={controlRows}
      />

      <div
        style={{
          display: "grid",
          gap: "18px",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
        }}
      >
        <DataTableCard
          eyebrow="Open Risks"
          title="Current open items and cautions"
          description="Open risk items stay visible here, but the page no longer reads like a long caution memo."
          columns={["Item", "Current read", "Status"]}
          rows={openRiskRows}
        />
        <DataTableCard
          eyebrow="Kill Conditions"
          title="Continuation stops remain explicit"
          description="These sensitivities still exist by design and remain the cleanest stop conditions in the risk stack."
          columns={["Gate", "Current read", "Meaning"]}
          rows={killRows}
        />
      </div>

      <section
        style={{
          borderRadius: "24px",
          padding: "22px",
          background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
        }}
      >
        <div className="stack-md">
          <div className="stack-xs">
            <span className="card-eyebrow">Lower-Priority Detail</span>
            <h3 className="card-title">Why continuation still holds, and where to go next if a gate still matters.</h3>
            <p className="card-copy">
              This lower section keeps the continuation logic and follow-up surfaces available without letting supporting downside detail compete with the main investor narrative.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gap: "18px",
              gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
            }}
          >
            <DataTableCard
              eyebrow="Continuation Read"
              title="Why the current recommendation still holds"
              description="Continuation is still justified, but only under discipline."
              columns={["Support", "Current read"]}
              rows={continuationRows}
              footer={
                <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.9rem", lineHeight: 1.55 }}>
                  Visible controls still active: {openControlCount}. Interpretation remains managed-risk continuation, not optimism.
                </div>
              }
            />

            <div
              style={{
                borderRadius: "22px",
                padding: "20px",
                background: "linear-gradient(180deg, rgba(9, 14, 26, 0.88), rgba(7, 11, 21, 0.78))",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: "0 16px 34px rgba(3, 8, 20, 0.18)"
              }}
            >
              <div className="stack-sm">
                <div className="stack-xs">
                  <span className="card-eyebrow">Follow-up Surfaces</span>
                  <h3 className="card-title">Open the specific page that still controls the answer.</h3>
                </div>
                <p className="card-copy">
                  Financials still carries return discipline. Site &amp; Zoning still carries the land-side gates. This risk page stays concise by pointing back to the exact supporting layer that still matters.
                </p>
                <div className="row-wrap gap-sm">
                  <NavLink to="/financials" className="link-button link-button-secondary">
                    Financials
                  </NavLink>
                  <NavLink to="/site-zoning" className="link-button link-button-secondary">
                    Site &amp; Zoning
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function RiskPage() {
  const { data, loading, error } = useContent("risk-page", api.getRisk);

  return (
    <PageContainer
      eyebrow="Appendix / Risks"
      title="Bounded downside, explicit gates."
      lede="This appendix screen keeps the downside frame visible after the primary thesis, Financials, Team & Ask, and Market Support are already clear."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading risks and mitigants...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <RiskContent data={data} /> : null}
    </PageContainer>
  );
}
